import { createContext, useContext, useEffect, useState } from 'react';
import supabase from './supabaseClient';
import { useUserProfile } from './UserProfile';

const RevalidationDataContext = createContext();

export const RevalidationDataProvider = ({ children }) => {
  const { profile } = useUserProfile();

  const [phlHours, setPHLHours] = useState("0");
  const [totalHours, setTotalHours] = useState("0");
  const [totalParticipate, setTotalParticipate] = useState("0");
  const [selectedQualifications, setSelectedQualifications] = useState(0);
  const [renewalDate, setRenewalDate] = useState(null);
  const [lastRenewal, setLastRenewal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [reflective, setReflective] = useState([]);
  const [discussion, setDiscussion] = useState([]);
  const [confirmation, setConfirmation] = useState([]);

  const [phl, setPhl] = useState([]);
  const [cpd, setCpd] = useState([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");


  const fetchData = async () => {
    setLoading(true);

    try {
      // Fetch renewal dates and qualifications
      const [{ data: renewalData }, { data: qualData }] = await Promise.all([
        supabase
          .from('reval_members')
          .select('renewal_date, last_renewal_date')
          .eq('id', profile.id),

        supabase
          .from('reval_member_qualifications')
          .select('qualification_id')
          .eq('reval_member_id', profile.reval_members?.id || 0),
      ]);

      setSelectedQualifications(qualData?.length || 0);

      const renewal = renewalData?.[0] || {};
      setRenewalDate(renewal.renewal_date || null);
      setLastRenewal(renewal.last_renewal_date || null);

      // Fetch totals via Postgres functions
      const [
        { data: phlData },
        { data: cpdData },
        { data: partData },
        { data: reflectiveData },
        { data: feedbackData },
        { data: discussionData },
        { data: confirmationData }
      ] = await Promise.all([
        supabase.rpc('get_phl_hours_between_renewal', { user_id: profile.id }),
        supabase.rpc('get_cpd_hours_between_renewal', { user_id: profile.id }),
        supabase.rpc('get_participatory_hours_between_renewal', { user_id: profile.id }),
        supabase.storage.from('reflective-written-accounts').list(profile.id),
        supabase.rpc('get_feedback_between_renewal', { user_uuid: profile.id }),
        supabase.storage.from('reflective-discussion').list(profile.id),
        supabase.storage.from('confirmation').list(profile.id)
      ]);

      const start = new Date(lastRenewal);
      const end = new Date(renewalDate);
      if (isNaN(start) || isNaN(end)) {
        console.warn("Invalid date range for filtering files");
        setReflective([]);
        setDiscussion([]);
        setConfirmation([]);
      } else {
        const filteredReflective = (reflectiveData || []).filter(file => {
          const created = new Date(file.created_at);
          return created >= start && created <= end;
        });

        const filteredDiscussion = (discussionData || []).filter(file => {
          const created = new Date(file.created_at);
          return created >= new Date(lastRenewal) && created <= new Date(renewalDate);
        });

        const filteredConfirmation = (confirmationData || []).filter(file => {
          const created = new Date(file.created_at);
          return created >= new Date(lastRenewal) && created <= new Date(renewalDate);
        });
          
          console.log("Raw CPD hours:", cpdData, typeof cpdData);

          setPHLHours((phlData || 0).toLocaleString());
          setTotalHours((cpdData || 0).toLocaleString());
          setTotalParticipate((partData || 0).toLocaleString());
          setReflective(filteredReflective);
          setDiscussion(filteredDiscussion);
          setConfirmation(filteredConfirmation);
          setFeedback(feedbackData);
          await fetchCpd();
          await fetchPhl();
      }

    } catch (error) {
      console.error("Error fetching revalidation data", error);
    } finally {
      setLoading(false);
    }
  };

  //Fetch PHL
  const fetchPhl = async () => {
    if (!lastRenewal || !renewalDate) return;

    const { data, error } = await supabase
        .from('phl_entries')
        .select()
        .eq('user_id', profile.id)
        .gte('date', lastRenewal)
        .lte('date', renewalDate);

    if (error) {
        console.error(error);
        setError('Error fetching PHL data.');
    } else {
        setPhl(data);
        setError("");
    }}
  
  //Fetch CPD data from Supabase
  const fetchCpd = async () => {
  const { data, error } = await supabase
      .from('cpd_entries')
      .select()
      .eq('user_id', profile.id)
      .gte('date', lastRenewal)
      .lte('date', renewalDate);

  if (error) {
      console.error(error);
      setError('Error fetching CPD data.');
  } else {
      setCpd(data);
  }};
  
  useEffect(() => {
    if (profile?.id && lastRenewal && renewalDate) {
      fetchPhl();
      fetchCpd();
    }
  }, [profile, lastRenewal, renewalDate]);

  useEffect(() => {
    if (profile?.id && profile.revalidation_membership) {
      fetchData();
    }
  }, [profile]);

  return (
    <RevalidationDataContext.Provider value={{
      phlHours,
      totalHours,
      totalParticipate,
      selectedQualifications,
      renewalDate,
      lastRenewal,
      loading,
      feedback,
      reflective,
      discussion,
      confirmation,
      phl,
      cpd,
      refresh: fetchData
    }}>
      {children}
    </RevalidationDataContext.Provider>
  );
};

export const useRevalidationData = () => useContext(RevalidationDataContext);